import { EMAIL_KINDS } from '../pages/contactMethods';
import { telHref } from '../records/recordPicture';

/**
 * 🔒 Set Meeting on the Intent card (piece 19; decision 6001; row T2778). Intent
 * is where the Discovery visit is set: the act books it as an `Event` tagged to
 * the Deal, with the Deal's people, its site and the Physical kind, through the
 * calendar's own window. Mark Intent Complete asks for the meeting unless one is
 * set or the person ticks *It came up on the sales call*, which is kept as a
 * `Note` on the Deal under the section key below with a fixed text.
 */
export const INTENT_MEETING_SECTION_KEY = 'Intent';

/** One person the meeting starts with: the Deal's Authority people and its open contacts. */
export interface DealMeetingPerson {
  personId: string;
  name: string;
}

/** 🔒 What the Intent card reads of the meeting, from the Deal page's one answer. */
export interface DealMeetingView {
  /** The newest uncanceled event tagged to the Deal, or null where none is set. */
  set: { eventId: string; subject: string; startsAt: string } | null;
  /** True once *It came up on the sales call* was ticked on Mark Intent Complete. */
  salesCall: boolean;
  /** The title a new meeting opens with: the Discovery visit and the Deal's name. */
  subject: string;
  /** The Deal's site, the place a new meeting opens on; null where the Deal names none. */
  siteId: string | null;
  people: readonly DealMeetingPerson[];
}

/** 🔒 Mark Intent Complete asks for the meeting where none is set and the sales call was not ticked. */
export function meetingAsked(meeting: Pick<DealMeetingView, 'set' | 'salesCall'> | null | undefined): boolean {
  if (!meeting) return false;
  return meeting.set === null && !meeting.salesCall;
}

/** The people a meeting starts with, each once, in the order handed: Authority first, then the contacts. */
export function meetingPeople(groups: readonly (readonly DealMeetingPerson[])[]): DealMeetingPerson[] {
  const seen = new Set<string>();
  const out: DealMeetingPerson[] = [];
  for (const person of groups.flat()) {
    if (seen.has(person.personId)) continue;
    seen.add(person.personId);
    out.push(person);
  }
  return out;
}

/** 🔒 How an Authority person is reached (decision 6001): the first number and the first address of their lines at the buying company. */
export interface AuthorityReach {
  phone: string | null;
  phoneExtension: string | null;
  email: string | null;
}

const PHONE_KINDS = ['Desk', 'Mobile'] as const;

/**
 * The line a Call and an Email reach, as the phone's record header picks them: a
 * primary line first, then the first in the order handed. A fax and an Other
 * line reach nothing.
 */
export function authorityReach(lines: readonly { kind: string; value: string; extension: string | null; isPrimary: boolean }[]): AuthorityReach {
  const ordered = [...lines.filter((line) => line.isPrimary), ...lines.filter((line) => !line.isPrimary)].filter((line) => line.value.trim() !== '');
  const phone = ordered.find((line) => (PHONE_KINDS as readonly string[]).includes(line.kind)) ?? null;
  const email = ordered.find((line) => (EMAIL_KINDS as readonly string[]).includes(line.kind)) ?? null;
  return { phone: phone?.value.trim() ?? null, phoneExtension: phone?.extension ?? null, email: email?.value.trim() ?? null };
}

/** 🔒 The two quick buttons' addresses, each null where the person carries no such line, so its button is not drawn. */
export function authorityLinks(reach: Partial<AuthorityReach> | null | undefined): { call: string | null; email: string | null } {
  return {
    call: reach?.phone ? telHref(reach.phone, reach.phoneExtension ?? null) : null,
    email: reach?.email ? `mailto:${reach.email}` : null,
  };
}
